/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { dialogComponents } from 'components/Dialog/dialogComponents';
import { useModalActions } from 'components/ModalManager/Context';
import { Select } from 'components/Select';
import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'components/Button';
import { flex } from 'styles/utils';
import { cx } from '@linaria/core';
import { IconDollar } from 'components/Icons';
import { checkIfPatHasComputeAccess, createFourDigitRandomString, getProviderData, makeCpuTemplate, makeGpuTemplate } from 'modules/Compute/utils';
import { either, noop, pipe } from 'utils/fp';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { nodepoolsApiEffects } from 'api/nodepools/apiEffects';
import { Loader, LoadingOverlay } from '@mantine/core';
import { useCurrentUserId, useCurrentUser } from 'modules/Auth/hooks';
import { CreateNodepoolBody } from 'api/nodepools/types';
import { useMutation, useQueryClient } from 'react-query';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { clustersApiEffects } from 'api/computeClusters/apiEffects';
import { ComputeClusters } from 'api/computeClusters/types';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { useBoolean } from 'react-use';
import { formatPricingPerHour } from 'utils/pricing';
import { useRouter } from 'next/router';
import { HUBSPOT_CUSTOM_EVENTS_NAME, sendEvent } from 'utils/hubspotApi/hubspotApi';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { pinkText } from './Plan.styles';

import {
  computeDetail,
  description,
  details,
  detailsText,
  modalFooter,
  option,
  selectStyles,
  titleStyles,
  titleWrapper,
} from './SetupComputeModal.styles';
import { getNewPATDescription, NodePoolDetails, SetupComputeMockData, SetupComputeType } from '../constants';
import { RestrictBillingPlan, UpgradePlanWrapper } from './RestrictBillingPlan';
import { EasyDeployModal } from './EasyDeployModal';
import { useTrendingModels } from '../hooks/useTrendingModels';
import { FormatOptionForPAT } from './FormatOptionForPAT';
import { usePATOptions } from './usePATOptions';

type SetupComputeModalProps = {
  closeAction: () => void;
  setupComputeType: SetupComputeType;
};

const SETUP_COMPUTE_MODAL_ID = 'setup_compute_modal';

export function FormatOption(props: Pick<SetupComputeModalProps, 'setupComputeType'>) {
  const computeTypeDetails = SetupComputeMockData.find((data) => data.setupComputeType === props.setupComputeType);

  if (props.setupComputeType === SetupComputeType.Basic) {
    return (
      <span className={option}>
        {computeTypeDetails?.iconSmall}
        Basic Compute <strong>(Recommended)</strong>
      </span>
    );
  }

  return <span className={option}>{computeTypeDetails?.iconSmall} Advanced Compute</span>;
}

export function ComputeDetail({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={computeDetail}>
      <strong>{title}</strong>
      {children}
    </div>
  );
}

export function ComputeDetails({ computeType, nodepoolDetails }: { computeType: SetupComputeType; nodepoolDetails: NodePoolDetails }) {
  const computeTypeDetails = SetupComputeMockData.find((data) => data.setupComputeType === computeType);
  const [providerData, regionData] = getProviderData(nodepoolDetails.providerId, nodepoolDetails.regionId);
  const { data: instanceTypesE, isFetching } = useQueryTE(
    [queryKeys.InstanceTypes, { cloudProviderId: nodepoolDetails.providerId, region: nodepoolDetails.regionId }],
    nodepoolsApiEffects.listInstanceTypesTE(nodepoolDetails.providerId, nodepoolDetails.regionId),
    {
      staleTime: 1000 * 60,
    },
  );

  const instance = pipe(
    instanceTypesE,
    either.fold(
      () => undefined,
      (instanceTypesResp) => instanceTypesResp.find((instanceType) => instanceType.id === nodepoolDetails.instanceId),
    ),
  );

  return (
    <div className={details}>
      <LoadingOverlay visible={isFetching} loader={<Loader variant="dots" size="md" />} />
      <p className={detailsText}>{computeTypeDetails?.body}</p>
      <ComputeDetail title="Provider:">{providerData.component}</ComputeDetail>
      <ComputeDetail title="Region:">{regionData.component}</ComputeDetail>
      <ComputeDetail title="Instance:">{instance?.id}</ComputeDetail>
      <ComputeDetail title="CPUs:">{instance?.compute_info && makeCpuTemplate({ instance: instance.compute_info })}</ComputeDetail>
      <ComputeDetail title="GPUs:">{instance?.compute_info && makeGpuTemplate({ instance: instance.compute_info })}</ComputeDetail>
      <ComputeDetail title="Instance Scaling:">
        Min: {nodepoolDetails.minInstances}, Max: {nodepoolDetails.maxInstances}
      </ComputeDetail>
      <ComputeDetail title="Cost:">
        <div className={cx(flex.init, flex.alignItems.center)}>
          <IconDollar data-testid="icon-dollar" size={12} color="#aa39df" />

          <strong className={cx(pinkText)}>{instance?.price && formatPricingPerHour(Number.parseFloat(instance?.price))} / instance / hour</strong>
        </div>
      </ComputeDetail>
    </div>
  );
}

export function SetupComputeModal(props: SetupComputeModalProps) {
  const [computeType, setComputeType] = useState(props.setupComputeType);
  const [showDeployModel, setShowDeployModel] = useBoolean(false);
  const { nodePoolsRestrictInstancesLDFF } = useFeatureFlagsLD();
  const router = useRouter();
  const userId = useCurrentUserId();
  const { track } = useDataCollection();
  const user = useCurrentUser();
  const { trendingModels: models } = useTrendingModels();
  const queryClient = useQueryClient();
  const [configForDeployModel, setConfigForDeployModel] = useState<{
    clusterId: string;
    nodepoolId: string;
    providerId: string;
    regionId: string;
    computeType: string;
  } | null>(null);
  const {
    patOptions,
    selectedPATOption,
    setSelectedPatOption,
    createNewPAT,
    isLoading: isPATLoading,
  } = usePATOptions({
    userId: userId as string,
    newPatDescription: getNewPATDescription(computeType),
    filterPAT: checkIfPatHasComputeAccess,
  });

  const trendingModels = models.filter((model) => computeType && model.supportedComputeTypes.includes(computeType));

  const computeTypeDetails = useMemo(() => SetupComputeMockData.find((data) => data.setupComputeType === computeType), [computeType]);
  const nodepoolDetails = useMemo(
    () => ({ ...computeTypeDetails?.nodepoolDetails!, ...(nodePoolsRestrictInstancesLDFF ? { minInstances: 0 } : {}) }),
    [nodePoolsRestrictInstancesLDFF, computeTypeDetails],
  );

  const closeModalAndRedirectToNodepoolsPage = (clusterId: string, nodepoolId: string) => {
    props.closeAction();
    router.push(`/compute/clusters/${encodeURIComponent(clusterId)}/nodepools/${nodepoolId}`);
  };

  // Copying some code from: modules/User/components/Settings/ComputeClusters/ComputeClusterForm/utils/hooks.ts
  const { mutate, isLoading } = useMutation(
    async ({ nodepoolDetails }: { nodepoolDetails: NodePoolDetails }) => {
      const computeTypeSlug = computeType?.toLowerCase();
      const clusterId = `${computeTypeSlug}-cluster-${createFourDigitRandomString()}`;
      const nodepoolId = `${computeTypeSlug}-nodepool-${createFourDigitRandomString()}`;

      const keyId = await createNewPAT();

      const computeClusterBody: ComputeClusters = {
        compute_clusters: [
          {
            id: clusterId,
            cloud_provider: {
              id: nodepoolDetails.providerId,
              name: getProviderData(nodepoolDetails.providerId, nodepoolDetails.regionId)[0].name,
            },
            region: nodepoolDetails.regionId,
            cluster_type: 'dedicated',
            managed_by: 'clarifai',
            user_id: userId,
            key: { id: keyId as string },
            visibility: {
              gettable: 10,
            },
          },
        ],
      };

      const createNodepoolBody: CreateNodepoolBody = {
        nodepools: [
          {
            id: nodepoolId,
            node_capacity_type: {
              capacity_types: [1],
            },
            instance_types: [
              {
                id: nodepoolDetails.instanceId,
              },
            ] as any,

            min_instances: nodepoolDetails.minInstances,
            max_instances: nodepoolDetails.maxInstances,
            visibility: {
              gettable: 10,
            },
          },
        ],
      };

      const isClusterCreated = await clustersApiEffects.createCluster(userId as string, computeClusterBody);
      if (!isClusterCreated) {
        return null;
      }
      track('New Cluster Created', {
        clusterId,
        compute_type: computeType,
        compute_region: nodepoolDetails.regionId,
        compute_provider: nodepoolDetails.providerId,
      });
      cogoToast.success(`Cluster ${clusterId} created successfully.`);

      const isNodepoolCreated = await nodepoolsApiEffects.createNodepool(userId, clusterId, createNodepoolBody);
      if (!isNodepoolCreated) {
        return null;
      }
      cogoToast.success(`Nodepool ${nodepoolId} created successfully.`);
      sendEvent({
        email: user?.email,
        eventName: HUBSPOT_CUSTOM_EVENTS_NAME.coClusterCreated,
        properties: {
          compute_type: computeType,
          compute_region: nodepoolDetails.regionId,
          compute_provider: nodepoolDetails.providerId,
        },
      });

      return { clusterId, nodepoolId, providerId: nodepoolDetails.providerId, regionId: nodepoolDetails.regionId, computeType };
    },
    {
      onSuccess: (data) => {
        if (data) {
          cogoToast.success(`${computeType} Compute created successfully.`);
          queryClient.invalidateQueries(['MyCompute', { userOrOrgId: userId }]);
          setShowDeployModel(true);
          setConfigForDeployModel(data);
        }
      },
    },
  );

  if (showDeployModel && configForDeployModel) {
    return (
      <EasyDeployModal
        modelsList={trendingModels}
        onCancel={() => {
          closeModalAndRedirectToNodepoolsPage(configForDeployModel.clusterId, configForDeployModel.nodepoolId);
        }}
        closeAction={() => {
          closeModalAndRedirectToNodepoolsPage(configForDeployModel.clusterId, configForDeployModel.nodepoolId);
        }}
        clusterId={configForDeployModel.clusterId}
        nodepoolId={configForDeployModel.nodepoolId}
        regionId={configForDeployModel.regionId}
        providerId={configForDeployModel.providerId}
        computeType={configForDeployModel.computeType}
      />
    );
  }

  return (
    <RestrictBillingPlan
      requiredScopes={computeTypeDetails?.requiredScopes}
      requiredFeaturedFlags={computeTypeDetails?.requiredFeaturedFlags}
      closeAction={props.closeAction}
    >
      <div>
        <div className={titleWrapper}>
          <h3 className={titleStyles}>Confirm Compute Selection</h3>
          <dialogComponents.CloseButton onClick={props.closeAction} />
        </div>
        <p className={description}>Review your compute instance details before proceeding.</p>
        <Select
          modal
          title={<FormatOption setupComputeType={computeType} />}
          onChange={({ value }) => setComputeType(value as SetupComputeType)}
          options={[
            { name: 'Basic Compute', value: SetupComputeType.Basic, content: <FormatOption setupComputeType={SetupComputeType.Basic} /> },
            { name: 'Advanced Compute', value: SetupComputeType.Advanced, content: <FormatOption setupComputeType={SetupComputeType.Advanced} /> },
          ]}
          className={selectStyles}
        />
        {!!userId && (
          <Select
            modal
            title={<FormatOptionForPAT pat={selectedPATOption ?? ''} isLoading={isPATLoading} />}
            onChange={({ value }) => setSelectedPatOption(value as SetupComputeType)}
            options={patOptions}
            className={selectStyles}
          />
        )}

        <ComputeDetails computeType={computeType} nodepoolDetails={nodepoolDetails} />

        <div className={modalFooter}>
          <Button size="md" variant="tertiary" onClick={props.closeAction}>
            Cancel
          </Button>
          <UpgradePlanWrapper>
            <Button size="md" loading={isLoading} disabled={isLoading} onClick={() => mutate({ nodepoolDetails })}>
              Create
            </Button>
          </UpgradePlanWrapper>
        </div>
      </div>
    </RestrictBillingPlan>
  );
}

export function useSetupComputeModal() {
  const { openModal, closeModal } = useModalActions();

  return useCallback((setupComputeType: SetupComputeType) => {
    openModal({
      id: SETUP_COMPUTE_MODAL_ID,
      title: '',
      content: <SetupComputeModal setupComputeType={setupComputeType} closeAction={() => closeModal({ id: SETUP_COMPUTE_MODAL_ID })} />,
      makeActions: noop,
    });
  }, []);
}
